import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Warning, FileText, ArrowsClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Phase 4 — driver-private invoice creation modal.
 *
 * Strict isolation:
 *   • Reads/writes only `/api/driver/invoices` and `/api/driver/personal-rides`.
 *   • Letterhead pulled from existing user profile via
 *     `/api/driver/business-profile-status`. No new business profile system.
 *   • If profile is incomplete (missing required legal fields), creation is
 *     blocked with a guided link to /profile.
 *
 * Stability fixes (2026-04-29):
 *   • Status is reloaded on every open so re-opening after editing the
 *     profile picks up the new state immediately.
 *   • Form is hydrated once per open/ride pair, NOT on every prop change —
 *     the user's typed values are no longer wiped by parent re-renders.
 *   • A "Recharger le profil" button lets the user manually retry without
 *     closing the dialog.
 */
export default function PersonalInvoiceModal({ open, ride, onClose, onCreated }) {
  // `ride.ride_source` is optional — "personal" (default) or "dispatch".
  // For dispatch rides the driver chooses an invoice target:
  //   • "dispatcher" → bill the company that dispatched the ride
  //   • "passenger"  → bill the passenger (typical for PAB rides)
  // Personal rides keep the original single-client flow (target = "client").
  const rideSource = ride?.ride_source === "dispatch" ? "dispatch" : "personal";
  const isDispatch = rideSource === "dispatch";
  const isPab = isDispatch && (ride?.is_pab || ride?.payment_type === "pab");
  const [target, setTarget] = useState(isDispatch ? (isPab ? "passenger" : "dispatcher") : "client");
  const [status, setStatus] = useState(null); // {complete, missing[], missing_labels[], profile{}}
  const [statusLoading, setStatusLoading] = useState(false);
  const [form, setForm] = useState({
    amount: "", client_name: "", client_address: "", notes: "", issued_at: "",
    // ─── Phase 33.1 · B2B billing ───
    billing_client_type: "individual",
    company_name: "", company_address: "", company_siret: "",
    company_contact_name: "", company_email: "", company_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [hydrationKey, setHydrationKey] = useState(""); // resets only when (open, ride.id) changes

  const reloadStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/driver/business-profile-status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  };

  // Refresh profile-status every time the modal opens — no stale snapshot.
  useEffect(() => {
    if (!open) return;
    setStatus(null); // clear stale data so the spinner shows a true reload
    reloadStatus();
  }, [open]);

  // Hydrate the form ONCE per modal opening (or per ride change) — never on
  // every parent re-render. Keying on `${open}-${ride.id}` guarantees the
  // user's typed values stay put as long as the dialog stays open.
  useEffect(() => {
    if (!open || !ride?.id) return;
    const key = `${open ? "1" : "0"}-${ride.id}`;
    if (key === hydrationKey) return;
    setHydrationKey(key);
    const today = new Date().toISOString().slice(0, 10);
    // Seed the target on open based on ride type (PAB-aware).
    const autoTarget = isDispatch ? (isPab ? "passenger" : "dispatcher") : "client";
    setTarget(autoTarget);
    // Seed amount from whichever field the ride carries.
    const amt = ride.price != null ? ride.price
              : ride.driver_amount != null ? ride.driver_amount
              : ride.driver_fare != null ? ride.driver_fare
              : "";
    setForm({
      amount: amt !== "" ? String(amt) : "",
      client_name: ride.client_name || ride.passenger_name || "",
      client_address: "",
      notes: "",
      issued_at: today,
      billing_client_type: autoTarget === "dispatcher" ? "company" : "individual",
      company_name: "", company_address: "", company_siret: "",
      company_contact_name: "", company_email: "", company_phone: "",
    });
  }, [open, ride, hydrationKey, isDispatch, isPab]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!ride?.id) return;
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Montant invalide"); return; }
    // Phase 33.1 — UI-side guard so the user gets immediate feedback.
    // The backend is the legal source of truth (re-validates at issuance).
    const isCompany = form.billing_client_type === "company";
    if (isCompany) {
      const missing = [];
      if (!form.company_name.trim())    missing.push("Raison sociale");
      if (!form.company_address.trim()) missing.push("Adresse");
      // SIREN (9 digits) or SIRET (14 digits) — mandatory for B2B.
      const siretDigits = (form.company_siret || "").replace(/\D+/g, "");
      if (!siretDigits) {
        missing.push("SIREN / SIRET");
      } else if (siretDigits.length !== 9 && siretDigits.length !== 14) {
        toast.error("Le numéro doit contenir 9 chiffres (SIREN) ou 14 chiffres (SIRET)");
        return;
      }
      if (missing.length) { toast.error(`Champs entreprise manquants: ${missing.join(", ")}`); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        amount,
        client_name: form.client_name || null,
        client_address: form.client_address || null,
        notes: form.notes || null,
        issued_at: form.issued_at || null,
        // Phase 33.1 — B2B billing payload (only sent when relevant)
        billing_client_type: form.billing_client_type,
        company_name:         isCompany ? form.company_name.trim()         : null,
        company_address:      isCompany ? form.company_address.trim()      : null,
        company_siret:        isCompany ? form.company_siret.trim()        : null,
        company_contact_name: isCompany ? form.company_contact_name.trim() : null,
        company_email:        isCompany ? form.company_email.trim()        : null,
        company_phone:        isCompany ? form.company_phone.trim()        : null,
      };
      if (isDispatch) {
        payload.dispatch_ride_id = ride.id;
        payload.invoice_target   = target;
      } else {
        payload.personal_ride_id = ride.id;
      }
      await api.post("/driver/invoices", payload);
      toast.success("Facture créée");
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const isComplete = status?.complete === true;
  const missingLabels = status?.missing_labels || status?.missing || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="personal-invoice-modal" className="bg-[#0f0f0f] border border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-base flex items-center gap-2">
            <FileText size={18} className="text-amber-400" /> Nouvelle facture
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Document personnel — émis à votre nom de chauffeur indépendant.
          </DialogDescription>
        </DialogHeader>

        {statusLoading && <p className="text-xs text-zinc-500">Chargement…</p>}

        {!statusLoading && status && !isComplete && (
          <div data-testid="invoice-profile-incomplete" className="border border-amber-500/40 bg-amber-500/10 rounded-md p-3 space-y-2">
            <div className="flex items-center gap-2 text-amber-200">
              <Warning size={16} />
              <p className="text-sm font-medium">Profil professionnel incomplet</p>
            </div>
            <p className="text-xs text-amber-100/80">
              Pour générer une facture, complétez les champs suivants dans votre profil :
            </p>
            <ul className="text-xs text-amber-100 list-disc pl-5">
              {missingLabels.map((label, idx) => (
                <li key={`${label}-${idx}`}>{label}</li>
              ))}
            </ul>
            <div className="flex gap-2 flex-wrap pt-1">
              <Button data-testid="invoice-profile-cta" onClick={() => window.location.assign("/profile")}
                className="bg-amber-500 text-black hover:bg-amber-600 font-semibold h-9 text-xs">
                Compléter mon profil
              </Button>
              <Button type="button" data-testid="invoice-profile-reload" onClick={reloadStatus} disabled={statusLoading}
                variant="outline" className="h-9 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                <ArrowsClockwise size={12} className={`mr-1.5 ${statusLoading ? "animate-spin" : ""}`} /> Recharger
              </Button>
            </div>
          </div>
        )}

        {!statusLoading && isComplete && ride && (
          <form onSubmit={submit} className="space-y-3">
            <div className="bg-[#121212] border border-zinc-800 rounded-md p-3 space-y-1 text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${isDispatch ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                  {isDispatch ? "Dispatch" : "Personnel"}
                </span>
                {isDispatch && isPab && (
                  <span data-testid="invoice-pab-badge" className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-orange-500/40 bg-orange-500/10 text-orange-300">
                    PAB
                  </span>
                )}
                <p className="text-zinc-500">Course associée</p>
              </div>
              <p className="text-white">{ride.client_name || ride.passenger_name || "(client sans nom)"} · {ride.pickup_date} · {ride.pickup_time}</p>
              <p className="text-zinc-400 truncate">{ride.pickup_location || ride.route_label || "—"}{ride.dropoff_location ? ` → ${ride.dropoff_location}` : ""}</p>
            </div>

            {/* ─── Invoice target selector (dispatch rides only) ─── */}
            {isDispatch && (
              <div data-testid="invoice-target-selector" className="space-y-2 p-3 rounded-md border border-zinc-800 bg-[#0d0d0d]">
                <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Facturer à</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="invoice-target-dispatcher"
                    onClick={() => {
                      setTarget("dispatcher");
                      setForm((p) => ({ ...p, billing_client_type: "company" }));
                    }}
                    className={`text-xs px-3 py-2.5 rounded-md border transition-colors ${
                      target === "dispatcher"
                        ? "bg-amber-500 text-black border-amber-500 font-semibold"
                        : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
                    }`}
                  >
                    Dispatcher (société)
                  </button>
                  <button
                    type="button"
                    data-testid="invoice-target-passenger"
                    onClick={() => {
                      setTarget("passenger");
                      setForm((p) => ({ ...p, billing_client_type: "individual" }));
                    }}
                    className={`text-xs px-3 py-2.5 rounded-md border transition-colors ${
                      target === "passenger"
                        ? "bg-amber-500 text-black border-amber-500 font-semibold"
                        : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
                    }`}
                  >
                    Passager {isPab && <span className="text-[9px] opacity-70">(PAB)</span>}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 italic">
                  {target === "dispatcher"
                    ? "La facture sera émise au nom du donneur d'ordre (société dispatch)."
                    : "La facture sera émise au nom du passager transporté."}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Montant TTC (EUR) *</Label>
                <Input data-testid="invoice-amount" type="number" inputMode="decimal" step="0.01" required value={form.amount} onChange={set("amount")}
                  className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date d'émission</Label>
                <Input data-testid="invoice-issued-at" type="date" value={form.issued_at} onChange={set("issued_at")}
                  className="bg-[#1A1A1A] border-zinc-700 text-white focus:border-amber-500 h-10" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nom du client</Label>
              <Input data-testid="invoice-client-name" value={form.client_name} onChange={set("client_name")} placeholder="(optionnel)"
                className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Adresse client</Label>
              <Input data-testid="invoice-client-address" value={form.client_address} onChange={set("client_address")} placeholder="(optionnel)"
                className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
            </div>

            {/* ─── Phase 33.1 · B2B billing toggle + company fields ─── */}
            <div className="space-y-3 p-3 rounded-md border border-zinc-800 bg-[#0d0d0d]">
              <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium">Facturer une entreprise</p>
                  <p className="text-[11px] text-zinc-500">B2B — la facture sera émise au nom de la société (donneur d'ordre).</p>
                </div>
                <input
                  data-testid="invoice-b2b-toggle"
                  type="checkbox"
                  checked={form.billing_client_type === "company"}
                  onChange={(e) => setForm(p => ({ ...p, billing_client_type: e.target.checked ? "company" : "individual" }))}
                  className="h-4 w-4 accent-amber-500 shrink-0"
                />
              </label>
              {form.billing_client_type === "company" && (
                <div className="space-y-3 pt-2 border-t border-zinc-800">
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Raison sociale *</Label>
                    <Input data-testid="invoice-company-name" required value={form.company_name} onChange={set("company_name")}
                      placeholder="Ex. ACME Transport SAS"
                      className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Adresse de facturation *</Label>
                    <Input data-testid="invoice-company-address" required value={form.company_address} onChange={set("company_address")}
                      placeholder="12 rue de Paris, 75001 Paris"
                      className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">SIREN / SIRET *</Label>
                    <Input data-testid="invoice-company-siret" required
                      value={form.company_siret}
                      onChange={(e) => {
                        // Keep digits only while typing; the UI shows up to
                        // 14 characters. The 9/14 length rule is enforced
                        // on submit + by the backend.
                        const digits = (e.target.value || "").replace(/\D+/g, "").slice(0, 14);
                        setForm((p) => ({ ...p, company_siret: digits }));
                      }}
                      inputMode="numeric"
                      pattern="\d{9}|\d{14}"
                      maxLength={14}
                      placeholder="9 chiffres (SIREN) ou 14 chiffres (SIRET)"
                      className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10 font-mono" />
                    {form.company_siret && form.company_siret.length !== 9 && form.company_siret.length !== 14 && (
                      <p data-testid="invoice-company-siret-error" className="text-[11px] text-red-400">
                        Le numéro doit contenir 9 chiffres (SIREN) ou 14 chiffres (SIRET)
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Contact</Label>
                      <Input data-testid="invoice-company-contact" value={form.company_contact_name} onChange={set("company_contact_name")}
                        placeholder="Nom (optionnel)"
                        className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Téléphone</Label>
                      <Input data-testid="invoice-company-phone" value={form.company_phone} onChange={set("company_phone")}
                        placeholder="(optionnel)"
                        className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-400 text-[11px] uppercase tracking-wider">Email</Label>
                    <Input data-testid="invoice-company-email" type="email" value={form.company_email} onChange={set("company_email")}
                      placeholder="(optionnel)"
                      className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10" />
                  </div>
                  <p className="text-[11px] text-amber-300/80">
                    Le passager (transporté) reste {ride.client_name ? <strong>{ride.client_name}</strong> : "votre client"}. La facture sera émise au nom de la société ci-dessus.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Mentions complémentaires</Label>
              <Textarea data-testid="invoice-notes" value={form.notes} onChange={set("notes")}
                placeholder="Conditions de paiement, références internes, etc."
                className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 min-h-[60px] text-sm" />
              <p className="text-[11px] text-zinc-500 italic">
                La mention TVA est ajoutée automatiquement selon votre profil — pas besoin de la saisir ici.
              </p>
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={onClose} className="border border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex-1">Annuler</Button>
              <Button type="submit" data-testid="invoice-submit" disabled={submitting} className="bg-amber-500 text-black hover:bg-amber-600 font-semibold flex-1">
                {submitting ? "..." : "Créer la facture"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
